import { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Gift, Star, ShoppingBag, MapPin, ChevronDown, ChevronUp, Trophy, Loader2, Percent } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { usePartnerOffers } from '@/hooks/use-partner-offers';
import { useCustomerPoints } from '@/hooks/use-customer-points';
import { format } from 'date-fns';

interface RewardsSystemProps {
  isExpanded?: boolean;
  onExpandChange?: (expanded: boolean) => void;
  organizationId?: string;
}

export function RewardsSystem({ isExpanded, onExpandChange, organizationId }: RewardsSystemProps) {
  const { t, dir } = useLanguage();
  const { offers, loading: offersLoading, error: offersError } = usePartnerOffers();
  const { points, history, loading: pointsLoading, error: pointsError, updateSelectedGoal } = useCustomerPoints();

  const handleSetGoal = async (rewardId: string) => {
    const reward = offers.find(r => r.id === rewardId);
    if (!reward) return;

    await updateSelectedGoal(rewardId, reward.pointsRequired);
  };

  const handleExpandToggle = () => {
    onExpandChange?.(!isExpanded);
  };

  const selectedReward = offers.find(r => r.id === points?.selectedGoalId);
  const rewardProgress = selectedReward ? Math.min(((points?.totalPoints || 0) / selectedReward.pointsRequired) * 100, 100) : 0;

  return (
    <Card>
      <CardHeader className="border-b bg-muted/50">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Gift className="h-5 w-5 text-primary" />
            {t('rewards.title')}
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline">
              <MapPin className="h-4 w-4 mr-2" />
              {t('rewards.partnerOffers')}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleExpandToggle}
            >
              {isExpanded ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
      </CardHeader>

      {isExpanded && (
        <CardContent className="p-6 grid gap-6 grid-cols-[2fr_1fr]">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold">{t('rewards.partnerOffers')}</h3>
              <div className="flex gap-2">
                <Badge variant="outline">{t('rewards.allCategories')}</Badge>
              </div>
            </div>
            
            {offersLoading ? (
              <div className="flex items-center justify-center p-8">
                <Loader2 className="h-8 w-8 animate-spin" />
              </div>
            ) : offersError ? (
              <div className="text-destructive text-center p-4">
                {offersError.message}
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                {offers.map((offer) => (
                  <motion.div
                    key={offer.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={cn(
                      "group relative overflow-hidden rounded-lg border bg-card transition-colors hover:bg-muted/50",
                      points?.selectedGoalId === offer.id && "ring-2 ring-[#7dda55]"
                    )}
                  >
                    <div className="relative">
                      {offer.imageUrl && (
                        <div className="aspect-video w-full overflow-hidden">
                          <img
                            src={offer.imageUrl}
                            alt={offer.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                      {offer.discountPercentage > 0 && (
                        <Badge className="absolute top-2 right-2 bg-primary text-white flex items-center gap-1">
                          <Percent className="h-3 w-3" />
                          {offer.discountPercentage}%
                        </Badge>
                      )}
                    </div>
                    <div className="p-4 space-y-2">
                      <h4 className="font-semibold">{offer.title}</h4>
                      <p className="text-sm text-muted-foreground">{offer.description}</p>
                      <div className="flex items-center justify-between">
                        <Badge variant="secondary">
                          {offer.pointsRequired} {t('rewards.points')}
                        </Badge>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleSetGoal(offer.id)}
                        >
                          {points?.selectedGoalId === offer.id 
                            ? t('rewards.currentGoal')
                            : t('rewards.setAsGoal')}
                        </Button>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {t('rewards.validUntil')}: {format(offer.validUntil, 'PPP')}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-4">
            <h3 className="font-semibold flex items-center gap-2">
              <Star className="h-4 w-4" />
              {t('rewards.recentActivity')}
            </h3>
            {pointsLoading ? (
              <div className="flex items-center justify-center p-8">
                <Loader2 className="h-8 w-8 animate-spin" />
              </div>
            ) : pointsError ? (
              <div className="text-destructive text-center p-4">
                {pointsError.message}
              </div>
            ) : (
              <ScrollArea className="h-[400px]">
                <div className="space-y-4">
                  {history.map((item, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: dir === 'rtl' ? -20 : 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className={cn(
                        "flex items-center justify-between p-3 rounded-lg border bg-card",
                        dir === 'rtl' && "flex-row-reverse"
                      )}
                    >
                      <div className={cn(
                        "space-y-1",
                        dir === 'rtl' && "text-right"
                      )}>
                        <div className="font-medium">{item.source}</div>
                        <div className="text-sm text-muted-foreground">
                          {item.date}
                        </div>
                      </div>
                      <Badge variant="secondary" className={cn(
                        "font-medium",
                        dir === 'rtl' && "mr-auto ml-0"
                      )}>
                        +{item.points}
                      </Badge>
                    </motion.div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );
}